import * as THREE from 'https://esm.sh/three@0.156.1';
import { GLTFLoader } from 'https://esm.sh/three@0.156.1/examples/jsm/loaders/GLTFLoader';
import {
  FaceLandmarker,
  FilesetResolver
} from 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/vision_bundle.mjs';


const MP_WASM_ROOT = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';
const MP_FACE_MODEL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

class ShopifyARSystem {
  constructor(root, options = {}) {
    this.root = root;
    this.stage = root.querySelector('.ar-system__stage');
    this.statusEl = root.querySelector('.ar-system__status');
    this.debugDot = root.querySelector('.ar-system__debug-dot');
    this.isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    this.leftHearingLevel = options.leftHearingLevel || '';
    this.rightHearingLevel = options.rightHearingLevel || '';
    this.selectedEar = options.selectedEar || '';
    this.requires85 = options.requires85 === true || options.productFilters?.requires85 === true;
    this.requires100 = options.requires100 === true || options.productFilters?.requires100 === true;
    this.answers = options.answers || {};
    this.productFilters = options.productFilters || this.buildProductFiltersFromAnswers(this.answers);
    this.SHOP_DOMAIN = root.dataset.shopDomain || window.location.hostname;
    this.STOREFRONT_TOKEN = root.dataset.storefrontToken || '';
    this.arProducts = [];
    this.allARProducts = [];
    this.currentProduct = null;
    this.productSelector = root.querySelector('[data-ar-product-selector]');
    this.productList = root.querySelector('[data-ar-product-list]');
    this.productToggle = root.querySelector('[data-ar-product-toggle]');
    this.productTitle = root.querySelector('[data-ar-products-title]');
    this.isProductSelectorCollapsed = false;


    this.buyButton = root.querySelector('[data-ar-buy-button]');

    this.DEFAULT_GLB_FILE = root.dataset.glbUrl || '';
    this.GLB_FILE = this.DEFAULT_GLB_FILE;
    this.RIGHT_EAR_ANCHOR = Number(root.dataset.rightEarAnchor || 356);
    this.LEFT_EAR_ANCHOR = Number(root.dataset.leftEarAnchor || 127);

    this.SMOOTH_POS = Number(root.dataset.smoothPos || 0.18);
    this.SMOOTH_ROT = Number(root.dataset.smoothRot || 0.08);

    this.FACE_POS_FOLLOW_SPEED = 30;
    this.FACE_ROT_FOLLOW_SPEED = 28;
    this.FACE_SCALE_FOLLOW_SPEED = 18;
    this.EAR_POS_FOLLOW_SPEED = 30;

    this.EAR_VISIBILITY_DX_THRESHOLD = 0.18;

    this.CAMERA_FOV = this.isMobile ? 40 : 50;
    this.CAMERA_NEAR = 0.01;
    this.CAMERA_FAR = 100;
    this.CAMERA_Z = 1.0;

    this.POS_STEP = 0.1;
    this.ROT_STEP_DEG = 3;
    this.TARGET_MODEL_SIZE_M = 0.08;

    this.FACE_LOCAL_SCALE_X = 0.34;
    this.FACE_LOCAL_SCALE_Y = 0.34;
    this.FACE_LOCAL_SCALE_Z = 0.24;
    this.BASE_EAR_DISTANCE = 0.060;

    this.REPROJECTION_STRENGTH_X = 0.0;
    this.REPROJECTION_STRENGTH_Y = 0.0;
    this.REPROJECTION_MAX_LOCAL_X = 0.0;
    this.REPROJECTION_MAX_LOCAL_Y = 0.0;
    this.REPROJECTION_SMOOTH = 0.18;

    this.faceLandmarker = null;
    this.video = null;
    this.stream = null;
    this.lastVideoTime = -1;
    this.running = false;

    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.faceAnchor = null;

    this.occDepthMat = null;
    this.occDebugMat = null;

    this.faceOcc = null;
    this.faceOccMain = null;
    this.faceOccUpper = null;

    this.smoothingEnabled = true;
    this.occlusion = {
      enabled: true,
      devMode: false,
      selectedPlane: 'top'
    };

    this.occlusionCalibrationActive = false;

    this.OCC_POS_STEP = 0.002;
    this.OCC_SCALE_STEP = 0.05;
    this.OCC_ROT_STEP_DEG = 3;
    this.occlusionState = {
      right: {
        top: {
          position: {
            x: 0.030,
            y: 0.058,
            z: -0.014
          },
          scale: {
            x: 0.099,
            y: 0.152,
            z: 1
          },
          rotation: {
            x: THREE.MathUtils.degToRad(0),
            y: THREE.MathUtils.degToRad(0),
            z: THREE.MathUtils.degToRad(0)
          }
        },
        canal: {
          position: {
            x: 0.013,
            y: 0.026,
            z: -0.038
          },
          scale: {
            x: 0.207,
            y: 0.174,
            z: 1
          },
          rotation: {
            x: THREE.MathUtils.degToRad(0),
            y: THREE.MathUtils.degToRad(129),
            z: THREE.MathUtils.degToRad(0)
          }
        }
      },
      left: {
        top: {
          position: {
            x: 0.030,
            y: 0.058,
            z: 0.014
          },
          scale: {
            x: 0.099,
            y: 0.152,
            z: 1
          },
          rotation: {
            x: THREE.MathUtils.degToRad(0),
            y: THREE.MathUtils.degToRad(0),
            z: THREE.MathUtils.degToRad(0)
          }
        },
        canal: {
          position: {
            x: 0.013,
            y: 0.028,
            z: 0.038
          },
          scale: {
            x: 0.207,
            y: 0.174,
            z: 1
          },
          rotation: {
            x: THREE.MathUtils.degToRad(0),
            y: THREE.MathUtils.degToRad(48),
            z: THREE.MathUtils.degToRad(0)
          }
        }
      }
    };

    /*
    * OCLUSÃO ESPECÍFICA DO OTICON ZEAL
    *
    * O Zeal:
    * - não utiliza plano Topo;
    * - utiliza apenas plano Canal;
    * - tem calibração própria para cada ouvido.
    */
   // zealOcclusionState.right.canal
    this.zealOcclusionState = {
      right: {
        canal: {
          position: {
            x: 0.003,
            y: 0.010,
            z: -0.040
          },
          scale: {
            x: 0.165,
            y: 0.242,
            z: 1
          },
          rotation: {
            x: THREE.MathUtils.degToRad(0),
            y: THREE.MathUtils.degToRad(102),
            z: THREE.MathUtils.degToRad(0)
          }
        }
      },
      left: {
        canal: {
          position: {
            x: 0.005,
            y: 0.010,
            z: 0.044
          },
          scale: {
            x: 0.165,
            y: 0.242,
            z: 1
          },
          rotation: {
            x: THREE.MathUtils.degToRad(0),
            y: THREE.MathUtils.degToRad(81),
            z: THREE.MathUtils.degToRad(0)
          }
        }
      }
    };

    this.MODEL_TRACKING_POINT = {
      x: 0.014,
      y: 0.018,
      z: 0.0
    };

    this.anchorDebugMesh = null;

    this._tmpRayOrigin = new THREE.Vector3();
    this._tmpRayDir = new THREE.Vector3();
    this._tmpPlanePoint = new THREE.Vector3();
    this._tmpPlaneNormal = new THREE.Vector3();
    this._tmpHitWorld = new THREE.Vector3();

    this._tmpWorldVec = new THREE.Vector3();
    this._tmpScreenVec = new THREE.Vector3();

    this.isRightEar = true;
    this.debugEnabled = false;
    this.debugLandmarkIndex = this.RIGHT_EAR_ANCHOR;
    this.latestLandmarks = null;

    this.faceMatrix = new THREE.Matrix4();
    this.fixMirrorMatrix = new THREE.Matrix4().makeScale(-1, 1, 1);

    this.targetFacePosition = new THREE.Vector3();
    this.smoothedFacePosition = new THREE.Vector3();

    this.targetFaceQuaternion = new THREE.Quaternion();
    this.smoothedFaceQuaternion = new THREE.Quaternion();

    this.targetFaceScale = new THREE.Vector3(1, 1, 1);
    this.smoothedFaceScale = new THREE.Vector3(1, 1, 1);
    this.lastFrameNowMs = 0;

    this.earNodes = {
      right: {
        hearingAidRoot: null,
        modelPivot: null,
        modelHolder: null,
        occlusionPivot: null,
        earOcc: null,
        earOccTop: null,
        earOccCanal: null,
        trackingAnchor: null,
        smoothedEarLocalPosition: new THREE.Vector3(),
        targetReprojectionOffset: new THREE.Vector3(),
        smoothedReprojectionOffset: new THREE.Vector3()
      },
      left: {
        hearingAidRoot: null,
        modelPivot: null,
        modelHolder: null,
        occlusionPivot: null,
        earOcc: null,
        earOccTop: null,
        earOccCanal: null,
        trackingAnchor: null,
        smoothedEarLocalPosition: new THREE.Vector3(),
        targetReprojectionOffset: new THREE.Vector3(),
        smoothedReprojectionOffset: new THREE.Vector3()
      }
    };

    this.earStateDesktop = {
      right: {
        sizeScale: 30.49,
        positionOffset: { x: 0.290, y: -1.796, z: -3.806 },
        rotationOffset: {
          x: THREE.MathUtils.degToRad(18),
          y: THREE.MathUtils.degToRad(192),
          z: THREE.MathUtils.degToRad(6)
        }
      },
      left: {
        sizeScale: 30.49,
        positionOffset: { x: -0.290, y: -1.696, z: -3.806 },
        rotationOffset: {
          x: THREE.MathUtils.degToRad(12),
          y: THREE.MathUtils.degToRad(-15),
          z: THREE.MathUtils.degToRad(6)
        }
      }
    };

    this.earStateMobile = {
      right: {
        sizeScale: 30.49,
        positionOffset: {
          x: 0.290,
          y: -1.496,
          z: -3.806
        },
        rotationOffset: {
          x: THREE.MathUtils.degToRad(18),
          y: THREE.MathUtils.degToRad(192),
          z: THREE.MathUtils.degToRad(6)
        }
      },

      left: {
        sizeScale: 30.49,
        positionOffset: {
          x: -0.290,
          y: -1.296,
          z: -3.806
        },
        rotationOffset: {
          x: THREE.MathUtils.degToRad(12),
          y: THREE.MathUtils.degToRad(-15),
          z: THREE.MathUtils.degToRad(6)
        }
      }
    };

    this.earState = this.isMobile
      ? this.cloneEarState(this.earStateMobile)
      : this.cloneEarState(this.earStateDesktop);

    this.bindUI();
  }

  async fetchARProducts() {
    const endpoint = `https://${this.SHOP_DOMAIN}/api/2026-04/graphql.json`;

    const query = `
      query ProductsWithAR($first: Int!) {
        products(first: $first) {
          nodes {
            id
            title
            handle
            productType
            category {
              id
              name
            }
            featuredImage {
              url
              altText
            }
            arGlbUrl: metafield(namespace: "custom", key: "ar_glb_url") {
              value
            }
            arRotationX: metafield(namespace: "custom", key: "ar_rotationx") {
              value
            }
            arRotationY: metafield(namespace: "custom", key: "ar_rotationy") {
              value
            }
            arRotationZ: metafield(namespace: "custom", key: "ar_rotationz") {
              value
            }
            arScale: metafield(namespace: "custom", key: "ar_scale") {
              value
            }

            arPosLX: metafield(namespace: "custom", key: "ar_pos_l_x") {
              value
            }
            arPosLY: metafield(namespace: "custom", key: "ar_pos_l_y") {
              value
            }
            arPosLZ: metafield(namespace: "custom", key: "ar_pos_l_z") {
              value
            }

            arPosRX: metafield(namespace: "custom", key: "ar_pos_r_x") {
              value
            }
            arPosRY: metafield(namespace: "custom", key: "ar_pos_r_y") {
              value
            }
            arPosRZ: metafield(namespace: "custom", key: "ar_pos_r_z") {
              value
            }
            batterySize: metafield(namespace: "shopify", key: "battery-type") {
              value
              references(first: 10) {
                nodes {
                  ... on Metaobject {
                    fields {
                      key
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.STOREFRONT_TOKEN
      },
      body: JSON.stringify({
        query,
        variables: {
          first: 100
        }
      })
    });

    const json = await response.json();

    if (json.errors) {
      throw new Error(json.errors.map((e) => e.message).join(', '));
    }

    return json.data.products.nodes
      .map((product) => {
        const batterySizeValues = [];

        product.batterySize?.references?.nodes?.forEach((node) => {
          node.fields?.forEach((field) => {
            if (field.value) {
              batterySizeValues.push(field.value);
            }
          });
        });

        return {
          id: product.id,
          title: product.title,
          handle: product.handle,
          url: `/products/${product.handle}`,
          productType: product.productType || '',
          categoryName: product.category?.name || '',
          image: product.featuredImage?.url || '',
          imageAlt: product.featuredImage?.altText || product.title,
          glbUrl: product.arGlbUrl?.value || '',
          rotationX: Number(product.arRotationX?.value || 0),
          rotationY: Number(product.arRotationY?.value || 0),
          rotationZ: Number(product.arRotationZ?.value || 0),
          scale: Number(product.arScale?.value || 1),

          posLeftX:
            product.arPosLX?.value != null
              ? Number(product.arPosLX.value)
              : null,

          posLeftY:
            product.arPosLY?.value != null
              ? Number(product.arPosLY.value)
              : null,

          posLeftZ:
            product.arPosLZ?.value != null
              ? Number(product.arPosLZ.value)
              : null,

          posRightX:
            product.arPosRX?.value != null
              ? Number(product.arPosRX.value)
              : null,

          posRightY:
            product.arPosRY?.value != null
              ? Number(product.arPosRY.value)
              : null,

          posRightZ:
            product.arPosRZ?.value != null
              ? Number(product.arPosRZ.value)
    : null,
          batterySizeRaw: product.batterySize?.value || '',
          batterySizeValues
        };
      });
  }

  filterProductsByHearingLevel(products) {
    let filtered = products || [];

    /*
    * Nunca mostrar acessórios no AR.
    */
    filtered = filtered.filter((product) => {
      return !this.productMatchesAccessoriesCategory(product);
    });

    /*
    * Perfuração do tímpano:
    * apenas miniBTE.
    *
    * O customer-exam.js já impede entrar aqui
    * quando existe Severa/Profunda.
    */
    if (this.productFilters?.onlyMbteCategory === true) {
      filtered = filtered.filter((product) => {
        return this.productMatchesMbteCategory(product);
      });
    } else {
      /*
      * Sem perfuração não mostrar miniBTE.
      */
      filtered = filtered.filter((product) => {
        return !this.productMatchesMbteCategory(product);
      });
    }

    /*
    * Se algum dos ouvidos tratáveis necessita nível 100,
    * só miniRITE consegue satisfazer a recomendação.
    *
    * Isto cobre:
    * - Severa + Moderada
    * - Severa + Ligeira
    * - Severa + Severa
    * - Profunda + Severa
    *
    * O ouvido Profundo é ignorado como ouvido tratável.
    */
    if (this.requires100) {
      filtered = filtered.filter((product) => {
        return this.productMatchesMiniRiteCategory(product);
      });
    }

    /*
    * Zeal só suporta nível 85.
    *
    * Portanto desaparece sempre que for necessário 100.
    */
    if (this.requires100) {
      filtered = filtered.filter((product) => {
        const title =
          this.normalizeFilterValue(product.title);

        return !title.includes('oticon zeal');
      });
    }

    /*
    * Filtro Bateria / Pilhas proveniente da pergunta 3.
    */
    if (this.productFilters?.batterySize) {
      const wantedBattery =
        this.normalizeFilterValue(
          this.productFilters.batterySize
        );

      filtered = filtered.filter((product) => {
        const productBatteryValues =
          this.parseBatterySizeValues(product);

        return productBatteryValues.includes(
          wantedBattery
        );
      });
    }

    return this.sortRecommendedProducts(filtered);
  }

  buildProductFiltersFromAnswers(answers) {
    const hasBatteryAnswer =
      answers?.['2'] === 'yes' ||
      answers?.['2'] === 'no';

    return {
      batterySize:
        hasBatteryAnswer
          ? (
              answers['2'] === 'yes'
                ? 'Bateria'
                : 'Pilhas'
            )
          : '',

      onlyMbteCategory:
        answers?.['6'] === 'yes'
    };
  }

  normalizeFilterValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  parseBatterySizeValues(product) {
    const values = [];

    if (Array.isArray(product.batterySizeValues)) {
      product.batterySizeValues.forEach((value) => {
        if (value) values.push(value);
      });
    }

    if (product.batterySizeRaw) {
      try {
        const parsed = JSON.parse(product.batterySizeRaw);
        if (Array.isArray(parsed)) {
          parsed.forEach((value) => {
            if (value) values.push(value);
          });
        } else if (parsed) {
          values.push(parsed);
        }
      } catch (_err) {
        values.push(product.batterySizeRaw);
      }
    }

    return values.map((value) => this.normalizeFilterValue(value));
  }

  productMatchesAccessoriesCategory(product) {
    const categoryText = [
      product.categoryName,
      product.productType,
      product.title
    ]
      .filter(Boolean)
      .join(' ');

    const normalized = this.normalizeFilterValue(categoryText);

    return (
      normalized.includes('hearing aid accessories') ||
      normalized.includes('accessories') ||
      normalized.includes('acessorios') ||
      normalized.includes('acessórios')
    );
  }

  productMatchesMiniRiteCategory(product) {
    const categoryText = [
      product.categoryName,
      product.productType,
      product.title
    ]
      .filter(Boolean)
      .join(' ');

    const normalized = this.normalizeFilterValue(categoryText);

    return (
      normalized.includes('receiver in canal') ||
      normalized.includes('receiver-in-canal') ||
      normalized.includes('ric') ||
      normalized.includes('minirite')
    );
  }

  productMatchesMbteCategory(product) {
    const categoryText = [
      product.categoryName,
      product.productType
    ]
      .filter(Boolean)
      .join(' ');

    const normalized = this.normalizeFilterValue(categoryText);

    return (
      normalized.includes('mini behind the ear') ||
      normalized.includes('mbte') ||
      normalized.includes('minibte')
    );
  }

  normalizeLevel(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  sortRecommendedProducts(products) {
    const order = this.productFilters?.recommendationOrder || 'best_first';

    return [...products].sort((a, b) => {
      const scoreA = this.getProductRecommendationScore(a, order);
      const scoreB = this.getProductRecommendationScore(b, order);

      return scoreA - scoreB;
    });
  }

  getProductRecommendationScore(product, order) {
    const title = this.normalizeFilterValue(product.title);

    if (this.productFilters?.prioritizeZeal && title.includes('oticon zeal')) {
      return -1000;
    }

    const baseRank = this.getBaseProductRank(title);
    const variantRank = this.getVariantRank(title);

    if (order === 'second_best_first') {
      return this.getSecondBestOrderRank(baseRank) * 10 + variantRank;
    }

    if (order === 'worst_first') {
      return this.getWorstFirstOrderRank(baseRank) * 10 + variantRank;
    }

    return baseRank * 10 + variantRank;
  }

  getBaseProductRank(title) {
    // Gama Premium
    if (title.includes('oticon zeal')) return 1;

    if (title.includes('oticon reveal 1')) return 2;
    if (title.includes('oticon intent 1')) return 3;
    if (title.includes('oticon verit 1')) return 4;

    if (title.includes('bernafon revita 400')) return 5;
    if (title.includes('bernafon encanta 400')) return 6;

    // Gama Alta / Intermédia-Alta
    if (title.includes('oticon reveal 2')) return 7;
    if (title.includes('oticon intent 2')) return 8;
    if (title.includes('oticon verit 2')) return 9;

    if (title.includes('bernafon revita 300')) return 10;
    if (title.includes('bernafon encanta 300')) return 11;

    // Gama Intermédia
    if (title.includes('oticon reveal 3')) return 12;
    if (title.includes('oticon intent 3')) return 13;
    if (title.includes('oticon verit 3')) return 14;

    if (title.includes('bernafon revita 200')) return 15;
    if (title.includes('bernafon encanta 200')) return 16;

    // Gama de Entrada
    if (title.includes('oticon reveal 4')) return 17;
    if (title.includes('oticon intent 4')) return 18;
    if (title.includes('oticon verit 4')) return 19;

    if (title.includes('bernafon revita 100')) return 20;
    if (title.includes('bernafon encanta 100')) return 21;

    if (title.includes('oticon jet 1')) return 22;
    if (title.includes('oticon jet 2')) return 23;

    return 999;
  }

  getVariantRank(title) {
    if (title.includes('minirite')) return 0;
    if (title.includes('minibte')) return 1;
    if (title.includes(' ite')) return 2;

    return 5;
  }

  getSecondBestOrderRank(baseRank) {
    const secondBestOrder = {
      // Começar pela gama Alta / Intermédia-Alta
      7: 1,
      8: 2,
      9: 3,
      10: 4,
      11: 5,

      // Depois gama Premium
      1: 6,
      2: 7,
      3: 8,
      4: 9,
      5: 10,
      6: 11,

      // Depois gama Intermédia
      12: 12,
      13: 13,
      14: 14,
      15: 15,
      16: 16,

      // Depois gama de Entrada
      17: 17,
      18: 18,
      19: 19,
      20: 20,
      21: 21,
      22: 22,
      23: 23
    };

    return secondBestOrder[baseRank] || 999;
  }

  getWorstFirstOrderRank(baseRank) {
    const worstFirstOrder = {
      // Começar pela gama de Entrada
      23: 1,
      22: 2,
      21: 3,
      20: 4,
      19: 5,
      18: 6,
      17: 7,

      // Depois gama Intermédia
      16: 8,
      15: 9,
      14: 10,
      13: 11,
      12: 12,

      // Depois gama Alta / Intermédia-Alta
      11: 13,
      10: 14,
      9: 15,
      8: 16,
      7: 17,

      // Finalmente gama Premium
      6: 18,
      5: 19,
      4: 20,
      3: 21,
      2: 22,
      1: 23
    };

    return worstFirstOrder[baseRank] || 999;
  }

  updateSelectedProductInfo() {
    const product = this.arProducts[this.selectedProductIndex || 0];

    if (this.buyButton) {
      this.buyButton.hidden = !product?.url;
    }
  }

  updateProductSelectorTitle() {
    if (!this.productTitle) return;

    this.productTitle.textContent =
      'Produtos recomendados';
  }

  renderProductSelector() {
    if (!this.productSelector || !this.productList || !this.arProducts.length) {
      return;
    }

    this.updateProductSelectorTitle();

    this.productSelector.hidden = false;

    this.productList.innerHTML = this.arProducts
      .map((product, index) => {
        const isActive = this.currentProduct?.id === product.id;

        const productImage = product.image
          ? `
            <img
              src="${this.escapeAttribute(product.image)}"
              alt="${this.escapeAttribute(product.imageAlt)}"
              loading="lazy"
            >
          `
          : `
            <span class="ar-system__product-placeholder">
              ${this.escapeHtml(product.title.charAt(0) || '?')}
            </span>
          `;

        return `
          <button
            type="button"
            class="ar-system__product-btn ${isActive ? 'is-active' : ''}"
            data-product-index="${index}"
            title="${this.escapeAttribute(product.title)}"
            aria-label="Experimentar ${this.escapeAttribute(product.title)}"
            aria-pressed="${isActive ? 'true' : 'false'}"
          >
            <span class="ar-system__product-btn-inner">
              <span class="ar-system__product-image-wrapper">
                ${productImage}
              </span>

              <span class="ar-system__product-item-content">
                <span class="ar-system__product-item-title">
                  ${this.escapeHtml(product.title)}
                </span>
              </span>
            </span>
          </button>
        `;
      })
      .join('');

    this.productList
      .querySelectorAll('[data-product-index]')
      .forEach((button) => {
        button.addEventListener('click', async () => {
          const index = Number(button.getAttribute('data-product-index'));

          await this.selectARProduct(index);
        });
      });

    this.updateSelectedProductInfo();

    if (this.buyButton && !this.buyButton.dataset.listenerAttached) {
      this.buyButton.dataset.listenerAttached = 'true';

      this.buyButton.addEventListener('click', () => {
        const product = this.arProducts[this.selectedProductIndex || 0];

        if (!product?.url) return;

        sessionStorage.setItem(
          'from_ar_recommendation',
          '1'
        );

        window.location.href = product.url;
      });
    }
  }

  async selectARProduct(index) {
    this.selectedProductIndex = index;
    const product = this.arProducts[index];
    if (!product) return;

    if (product.id === this.currentProduct?.id) {
      this.updateSelectedProductInfo();
      return;
    }

    this.currentProduct = product;

    /*
    * Zeal só utiliza o plano Canal.
    */
    if (this.isCurrentProductZeal()) {
      this.occlusion.selectedPlane = 'canal';

      const planeButton =
        this.root.querySelector(
          '[data-action="occ-switch-plane"]'
        );

      if (planeButton) {
        planeButton.textContent =
          '🟥 Plano: Canal';
      }
    }

    this.GLB_FILE = product.glbUrl || this.DEFAULT_GLB_FILE || this.root.dataset.glbUrl || '';
    
    this.updateSelectedProductInfo();

    this.productList
    ?.querySelectorAll('.ar-system__product-btn')
    .forEach((button) => {
      const buttonIndex = Number(
        button.getAttribute('data-product-index')
      );

      const isActive = buttonIndex === index;

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    const selectedButton = this.productList?.querySelector(
      `[data-product-index="${index}"]`
    );

    if (selectedButton) {
      const listRect = this.productList.getBoundingClientRect();
      const buttonRect = selectedButton.getBoundingClientRect();

      const isOutsideTop = buttonRect.top < listRect.top;
      const isOutsideBottom = buttonRect.bottom > listRect.bottom;

      if (isOutsideTop || isOutsideBottom) {
        selectedButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }

    this.updateStatus(`📦 A carregar ${product.title}...`);

    await this.loadGLBOnce();
    this.applyAllEarStates();
    this.updateCalibrationStatus(`✅ Produto ativo: ${product.title}`);
  }

  escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }

  cloneEarState(source) {
    return {
      right: {
        sizeScale: source.right.sizeScale,
        positionOffset: { ...source.right.positionOffset },
        rotationOffset: { ...source.right.rotationOffset }
      },
      left: {
        sizeScale: source.left.sizeScale,
        positionOffset: { ...source.left.positionOffset },
        rotationOffset: { ...source.left.rotationOffset }
      }
    };
  }

  updateStatus(msg) {
    if (this.statusEl) this.statusEl.textContent = msg;
  }

  getCurrentEarKey() {
    return this.isRightEar ? 'right' : 'left';
  }

  getCurrentState() {
    return this.earState[this.getCurrentEarKey()];
  }

  getCurrentEarAnchorIndex() {
    return this.isRightEar ? this.RIGHT_EAR_ANCHOR : this.LEFT_EAR_ANCHOR;
  }

  getEarAnchorIndex(earKey) {
    return earKey === 'right' ? this.RIGHT_EAR_ANCHOR : this.LEFT_EAR_ANCHOR;
  }

  getEarStateByKey(earKey) {
    return this.earState[earKey];
  }

  getLandmark(index) {
    return this.latestLandmarks?.[index] || null;
  }

  getEarCenterIndices() {
    if (this.isRightEar) return [356, 323];
    return [127, 93];
  }

  getEarCenterIndicesForKey(earKey) {
    if (earKey === 'right') return [356, 323];
    return [127, 93];
  }

  getEarLateralIndices() {
    if (this.isRightEar) return [356, 366];
    return [127, 137];
  }

  getEarLateralIndicesForKey(earKey) {
    if (earKey === 'right') return [356, 366];
    return [127, 137];
  }

  rebuildTrackingAnchorForKey(earKey) {
    const node = this.earNodes[earKey];
    if (!node || !node.modelHolder) return;

    if (node.trackingAnchor && node.trackingAnchor.parent) {
      node.trackingAnchor.parent.remove(node.trackingAnchor);
    }

    node.trackingAnchor = new THREE.Group();
    node.trackingAnchor.position.set(
      this.MODEL_TRACKING_POINT.x,
      this.MODEL_TRACKING_POINT.y,
      this.MODEL_TRACKING_POINT.z
    );

    node.modelHolder.add(node.trackingAnchor);
  }

  updateCalibrationStatus(prefix = '✅ AR ativo!') {
    const key = this.getCurrentEarKey();
    const st = this.earState[key];

    const position =
      this.getProductPositionForDevice(
        key
      );

    const rx = Math.round(THREE.MathUtils.radToDeg(st.rotationOffset.x));
    const ry = Math.round(THREE.MathUtils.radToDeg(st.rotationOffset.y));
    const rz = Math.round(THREE.MathUtils.radToDeg(st.rotationOffset.z));

    const earScale = this.getEarDistanceScaleMultiplier(key);

    this.updateStatus(
      `${prefix}\n` +
      `editEar=${key}\n` +
      `produto=${this.currentProduct?.title || 'manual'}\n` +
      `size=${st.sizeScale.toFixed(2)}\n` +
      `pos x=${position.x.toFixed(3)} y=${position.y.toFixed(3)} z=${position.z.toFixed(3)}\n` +
      `rot x=${rx}° y=${ry}° z=${rz}°\n` +
      `earScale=${earScale.toFixed(3)}\n` +
      `smooth=${this.smoothingEnabled ? 'ON' : 'OFF'}\n` +
      `occ=${
        this.occlusion.enabled
          ? (
              this.occlusion.devMode
                ? 'DEV'
                : 'ON'
            )
          : 'OFF'
      }`
    );
  }

  toggleProductSelector() {
    if (!this.productSelector || !this.productToggle) return;

    this.isProductSelectorCollapsed = !this.isProductSelectorCollapsed;

    this.productSelector.classList.toggle(
      'is-collapsed',
      this.isProductSelectorCollapsed
    );

    this.productToggle.textContent = this.isProductSelectorCollapsed ? '‹' : '›';

    this.productToggle.setAttribute(
      'aria-label',
      this.isProductSelectorCollapsed ? 'Mostrar produtos' : 'Esconder produtos'
    );
  }

  bindUI() {
    this.productToggle?.addEventListener('click', () => {
      this.toggleProductSelector();
    });
    this.root.querySelector('[data-action="switch-ear"]')?.addEventListener('click', () => this.switchEar());
    this.root.querySelector('[data-action="reset-all"]')?.addEventListener('click', () => this.resetAll());
    this.root.querySelector('[data-action="toggle-smoothing"]')?.addEventListener('click', (e) => this.toggleSmoothing(e.currentTarget));
    this.root.querySelector('[data-action="toggle-occlusion"]')?.addEventListener('click', (e) => this.toggleOcclusion(e.currentTarget));

    this.root.querySelector('[data-action="size-bigger"]')?.addEventListener('click', () => this.adjustSize('bigger'));
    this.root.querySelector('[data-action="size-smaller"]')?.addEventListener('click', () => this.adjustSize('smaller'));

    this.root.querySelector('[data-action="pos-up"]')?.addEventListener('click', () => this.adjustPosition('up'));
    this.root.querySelector('[data-action="pos-down"]')?.addEventListener('click', () => this.adjustPosition('down'));
    this.root.querySelector('[data-action="pos-forward"]')?.addEventListener('click', () => this.adjustPosition('forward'));
    this.root.querySelector('[data-action="pos-back"]')?.addEventListener('click', () => this.adjustPosition('back'));
    this.root.querySelector('[data-action="pos-left"]')?.addEventListener('click', () => this.adjustPosition('left'));
    this.root.querySelector('[data-action="pos-right"]')?.addEventListener('click', () => this.adjustPosition('right'));

    this.root.querySelector('[data-action="rot-x-plus"]')?.addEventListener('click', () => this.adjustRotation('x', 'plus'));
    this.root.querySelector('[data-action="rot-x-minus"]')?.addEventListener('click', () => this.adjustRotation('x', 'minus'));
    this.root.querySelector('[data-action="rot-y-plus"]')?.addEventListener('click', () => this.adjustRotation('y', 'plus'));
    this.root.querySelector('[data-action="rot-y-minus"]')?.addEventListener('click', () => this.adjustRotation('y', 'minus'));
    this.root.querySelector('[data-action="rot-z-plus"]')?.addEventListener('click', () => this.adjustRotation('z', 'plus'));
    this.root.querySelector('[data-action="rot-z-minus"]')?.addEventListener('click', () => this.adjustRotation('z', 'minus'));

    this.root.querySelector('[data-action="toggle-debug"]')?.addEventListener('click', (e) => this.toggleDebug(e.currentTarget));
    this.root.querySelector('[data-action="debug-prev"]')?.addEventListener('click', () => this.stepDebugLandmark(-1));
    this.root.querySelector('[data-action="debug-next"]')?.addEventListener('click', () => this.stepDebugLandmark(1));

    this.root
      .querySelector('[data-action="occ-switch-plane"]')
      ?.addEventListener(
        'click',
        (e) =>
          this.switchOcclusionPlane(
            e.currentTarget
          )
      );

    this.root
      .querySelector('[data-action="occ-dev"]')
      ?.addEventListener(
        'click',
        (e) =>
          this.toggleOcclusionDevMode(
            e.currentTarget
          )
      );

    /*
    * POSIÇÃO
    */
    this.root
      .querySelector('[data-action="occ-pos-up"]')
      ?.addEventListener(
        'click',
        () =>
          this.adjustOcclusionPosition('up')
      );

    this.root
      .querySelector('[data-action="occ-pos-down"]')
      ?.addEventListener(
        'click',
        () =>
          this.adjustOcclusionPosition('down')
      );

    this.root
      .querySelector('[data-action="occ-pos-left"]')
      ?.addEventListener(
        'click',
        () =>
          this.adjustOcclusionPosition('left')
      );

    this.root
      .querySelector('[data-action="occ-pos-right"]')
      ?.addEventListener(
        'click',
        () =>
          this.adjustOcclusionPosition('right')
      );

    this.root
      .querySelector('[data-action="occ-pos-forward"]')
      ?.addEventListener(
        'click',
        () =>
          this.adjustOcclusionPosition('forward')
      );

    this.root
      .querySelector('[data-action="occ-pos-back"]')
      ?.addEventListener(
        'click',
        () =>
          this.adjustOcclusionPosition('back')
      );

    /*
    * ESCALA
    */
    this.root
      .querySelector('[data-action="occ-scale-x-plus"]')
      ?.addEventListener(
        'click',
        () =>
          this.adjustOcclusionScale('x', 'plus')
      );

    this.root
      .querySelector('[data-action="occ-scale-x-minus"]')
      ?.addEventListener(
        'click',
        () =>
          this.adjustOcclusionScale('x', 'minus')
      );

    this.root
      .querySelector('[data-action="occ-scale-y-plus"]')
      ?.addEventListener(
        'click',
        () =>
          this.adjustOcclusionScale('y', 'plus')
      );

    this.root
      .querySelector('[data-action="occ-scale-y-minus"]')
      ?.addEventListener(
        'click',
        () =>
          this.adjustOcclusionScale('y', 'minus')
      );

    /*
    * ROTAÇÃO
    */
    ['x', 'y', 'z'].forEach((axis) => {
      this.root
        .querySelector(
          `[data-action="occ-rot-${axis}-plus"]`
        )
        ?.addEventListener(
          'click',
          () =>
            this.adjustOcclusionRotation(
              axis,
              'plus'
            )
        );

      this.root
        .querySelector(
          `[data-action="occ-rot-${axis}-minus"]`
        )
        ?.addEventListener(
          'click',
          () =>
            this.adjustOcclusionRotation(
              axis,
              'minus'
            )
        );
    });

    this.root
    .querySelector('[data-action="occ-copy-values"]')
    ?.addEventListener(
      'click',
      (e) =>
        this.copyOcclusionCalibrationValues(
          e.currentTarget
        )
    );
  }

  async setupMediaPipe() {
    const visionFiles = await FilesetResolver.forVisionTasks(MP_WASM_ROOT);

    this.faceLandmarker = await FaceLandmarker.createFromOptions(visionFiles, {
      baseOptions: {
        modelAssetPath: MP_FACE_MODEL,
        delegate: 'CPU'
      },
      runningMode: 'VIDEO',
      numFaces: 1,
      minFaceDetectionConfidence: 0.5,
      minFacePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: true
    });

    this.updateStatus('🧠 MediaPipe CPU + face matrix ativo');
  }

  async setupCamera() {
    this.video = document.createElement('video');
    this.video.setAttribute('autoplay', '');
    this.video.setAttribute('muted', '');
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('webkit-playsinline', '');
    this.video.style.position = 'absolute';
    this.video.style.inset = '0';
    this.video.style.width = '100%';
    this.video.style.height = '100%';
    this.video.style.objectFit = 'cover';
    this.video.style.zIndex = '1';
    this.video.style.transform = 'scaleX(-1)';
    this.stage.appendChild(this.video);

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 }
      }
    });

    this.video.srcObject = this.stream;

    await new Promise((resolve) => {
      this.video.onloadedmetadata = () => {
        this.video.play().then(resolve).catch(resolve);
      };
    });
  }

  setupThree() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    });

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.stage.clientWidth, this.stage.clientHeight);
    this.renderer.domElement.style.position = 'absolute';
    this.renderer.domElement.style.inset = '0';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.zIndex = '2';
    this.renderer.domElement.style.pointerEvents = 'none';
    this.stage.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(
      this.CAMERA_FOV,
      this.stage.clientWidth / this.stage.clientHeight,
      this.CAMERA_NEAR,
      this.CAMERA_FAR
    );
    this.camera.position.z = this.CAMERA_Z;

    this.scene.add(new THREE.AmbientLight(0xffffff, 1.2));

    const dir1 = new THREE.DirectionalLight(0xffffff, 0.9);
    dir1.position.set(0.5, 1.0, 1.0);
    this.scene.add(dir1);

    const dir2 = new THREE.DirectionalLight(0xffffff, 0.45);
    dir2.position.set(-0.5, 0.3, 0.8);
    this.scene.add(dir2);

    this.faceAnchor = new THREE.Group();
    this.scene.add(this.faceAnchor);

    const debugGeo = new THREE.SphereGeometry(0.01, 16, 16);
    const debugMat = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    this.anchorDebugMesh = new THREE.Mesh(debugGeo, debugMat);
    this.faceAnchor.add(this.anchorDebugMesh);

    ['right', 'left'].forEach((earKey) => {
      const node = this.earNodes[earKey];

      node.hearingAidRoot = new THREE.Group();
      node.occlusionPivot = new THREE.Group();
      node.modelPivot = new THREE.Group();
      node.modelHolder = new THREE.Group();

      node.modelPivot.add(node.modelHolder);
      node.hearingAidRoot.add(node.occlusionPivot);
      node.hearingAidRoot.add(node.modelPivot);

      this.faceAnchor.add(node.hearingAidRoot);
    });

    this.ensureOccMaterial();

    this.faceOcc = new THREE.Group();
    this.faceAnchor.add(this.faceOcc);

    this.faceOccMain = new THREE.Mesh(
      new THREE.SphereGeometry(1, 32, 24),
      this.occDepthMat
    );

    this.faceOccUpper = new THREE.Mesh(
      new THREE.SphereGeometry(1, 28, 20),
      this.occDepthMat
    );

    this.faceOcc.add(this.faceOccMain);
    this.faceOcc.add(this.faceOccUpper);

    this.createEarOccluderForKey('right');
    this.createEarOccluderForKey('left');
  }

  applyFaceOccluder() {
    if (!this.faceOcc || !this.faceOccMain || !this.faceOccUpper) return;

    this.faceOcc.visible = !!this.occlusion.enabled;
    if (!this.occlusion.enabled) return;

    const mat = this.occlusion.devMode ? this.occDebugMat : this.occDepthMat;
    this.faceOccMain.material = mat;
    this.faceOccUpper.material = mat;

    this.faceOccMain.position.set(0.0, 0.01, -0.015);
    this.faceOccMain.rotation.set(0, 0, 0);
    this.faceOccMain.scale.set(0.19, 0.24, 0.16);

    this.faceOccUpper.position.set(0.0, 0.095, -0.005);
    this.faceOccUpper.rotation.set(0, 0, 0);
    this.faceOccUpper.scale.set(0.13, 0.10, 0.10);
  }

  isEarLikelyVisible(earKey) {
    if (!this.latestLandmarks) return true;

    const ear = this.getLandmark(this.getEarAnchorIndex(earKey));
    const nose = this.getLandmark(1) || this.getLandmark(4);

    if (!ear || !nose) return true;

    const dx = Math.abs(ear.x - nose.x);
    return dx > this.EAR_VISIBILITY_DX_THRESHOLD;
  }

  ensureOccMaterial() {
    if (this.occDepthMat && this.occDebugMat) return;

    /*
    * Material REAL de oclusão.
    *
    * Não desenha cor, mas escreve no depth buffer.
    * Tudo o que estiver atrás do plano fica escondido.
    */
    this.occDepthMat = new THREE.MeshBasicMaterial({
      colorWrite: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide
    });

    /*
    * Material DEV.
    *
    * Vermelho transparente para conseguirmos ver
    * exatamente onde estão os planos.
    */
    this.occDebugMat = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.45,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide
    });
  }

  createEarOccluderForKey(earKey) {
    const node = this.earNodes[earKey];

    if (!node || !node.occlusionPivot) return;

    /*
    * Remover o occluder anterior.
    */
    if (node.earOcc) {
      node.occlusionPivot.remove(node.earOcc);

      node.earOcc.traverse((child) => {
        if (child.isMesh && child.geometry) {
          child.geometry.dispose();
        }
      });

      node.earOcc = null;
    }

    node.earOcc = new THREE.Group();

    /*
    * PLANO 1
    *
    * Topo / perpendicular à orelha.
    */
    node.earOccTop = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      this.occlusion.devMode
        ? this.occDebugMat
        : this.occDepthMat
    );

    /*
    * PLANO 2
    *
    * Canal / paralelo à orelha.
    */
    node.earOccCanal = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      this.occlusion.devMode
        ? this.occDebugMat
        : this.occDepthMat
    );

    /*
    * Renderizar os occluders antes do modelo.
    */
    node.earOccTop.renderOrder = -10;
    node.earOccCanal.renderOrder = -10;

    node.earOcc.add(node.earOccTop);
    node.earOcc.add(node.earOccCanal);

    node.occlusionPivot.add(node.earOcc);

    this.applyEarOccluderForKey(earKey);
  }

  landmarkToFaceLocal(lm) {
    return new THREE.Vector3(
      (0.5 - lm.x) * this.FACE_LOCAL_SCALE_X,
      (0.5 - lm.y) * -this.FACE_LOCAL_SCALE_Y,
      -lm.z * this.FACE_LOCAL_SCALE_Z
    );
  }

  getDtSeconds(nowMs) {
    if (!this.lastFrameNowMs) {
      this.lastFrameNowMs = nowMs;
      return 1 / 60;
    }

    const dt = (nowMs - this.lastFrameNowMs) / 1000;
    this.lastFrameNowMs = nowMs;

    return Math.min(Math.max(dt, 1 / 240), 1 / 20);
  }

  getFollowAlpha(speed, dt) {
    return 1 - Math.exp(-speed * dt);
  }

  getEarDistanceScaleMultiplier(earKey) {
    if (!this.latestLandmarks) return 1;

    const [idxA, idxB] = this.getEarLateralIndicesForKey(earKey);
    const a = this.getLandmark(idxA);
    const b = this.getLandmark(idxB);
    if (!a || !b) return 1;

    const p1 = this.landmarkToFaceLocal(a);
    const p2 = this.landmarkToFaceLocal(b);
    const dist = p1.distanceTo(p2);

    return THREE.MathUtils.clamp(dist / this.BASE_EAR_DISTANCE, 0.70, 1.55);
  }

  getEarScreenScaleMultiplier(earKey) {
    if (!this.latestLandmarks || !this.video) return 1;

    const [idxA, idxB] = this.getEarLateralIndicesForKey(earKey);

    const p1 = this.getLandmarkScreenPosition(idxA);
    const p2 = this.getLandmarkScreenPosition(idxB);

    if (!p1 || !p2) return 1;

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    const distPx = Math.sqrt(
      dx * dx +
      dy * dy
    );

    const normalizedDist =
      distPx / this.stage.clientHeight;

    const reference = 0.060;

    const rawScale =
      normalizedDist / reference;

    if (this.isMobile) {
      return THREE.MathUtils.clamp(
        rawScale,
        0.70,
        0.80
      );
    }

    return THREE.MathUtils.clamp(
      rawScale,
      0.55,
      1.8
    );
  }

  getScaledPositionOffset(st, dynamicScale) {
    if (this.isMobile) {
      /*
      * No mobile a posição acompanha apenas parcialmente
      * a mudança de escala.
      *
      * dynamicScale = 1.25
      * positionScale = 1.10
      *
      * em vez de multiplicar a posição diretamente por 1.25.
      */
      const positionScale =
        1 + (dynamicScale - 1) * 0.4;

      return {
        x: st.positionOffset.x * positionScale,
        y: st.positionOffset.y * positionScale,
        z: st.positionOffset.z * positionScale
      };
    }

    return {
      x: st.positionOffset.x * dynamicScale,
      y: st.positionOffset.y * dynamicScale,
      z: st.positionOffset.z * dynamicScale
    };
  }

  getEarLocalPositionFromScreenProjection(earKey) {
    if (!this.latestLandmarks || !this.camera || !this.faceAnchor) return null;

    const anchorIndex = this.getEarAnchorIndex(earKey);
    const lm = this.getLandmark(anchorIndex);
    if (!lm) return null;

    const screenPos = this.getLandmarkScreenPosition(anchorIndex);
    if (!screenPos) return null;

    const estimatedLocal = this.landmarkToFaceLocal(lm);
    const zLocal = estimatedLocal.z;

    const stageW = this.stage.clientWidth;
    const stageH = this.stage.clientHeight;
    if (!stageW || !stageH) return null;

    const ndcX = (screenPos.x / stageW) * 2 - 1;
    const ndcY = -(screenPos.y / stageH) * 2 + 1;

    this._tmpRayOrigin.setFromMatrixPosition(this.camera.matrixWorld);
    this._tmpRayDir.set(ndcX, ndcY, 0.5).unproject(this.camera).sub(this._tmpRayOrigin).normalize();

    this._tmpPlanePoint.set(0, 0, zLocal);
    this.faceAnchor.localToWorld(this._tmpPlanePoint);

    this._tmpPlaneNormal.set(0, 0, 1).transformDirection(this.faceAnchor.matrixWorld).normalize();

    const denom = this._tmpPlaneNormal.dot(this._tmpRayDir);
    if (Math.abs(denom) < 1e-6) return null;

    const t = this._tmpPlanePoint.clone().sub(this._tmpRayOrigin).dot(this._tmpPlaneNormal) / denom;
    if (t <= 0) return null;

    this._tmpHitWorld.copy(this._tmpRayOrigin).add(this._tmpRayDir.clone().multiplyScalar(t));

    return this.faceAnchor.worldToLocal(this._tmpHitWorld.clone());
  }

  getEarLocalPosition(earKey) {
    return this.getEarLocalPositionFromScreenProjection(earKey);
  }

  getLandmarkScreenPosition(index) {
    if (!this.latestLandmarks?.length || !this.video) return null;

    const lm = this.latestLandmarks[index];
    if (!lm) return null;

    const stageW = this.stage.clientWidth;
    const stageH = this.stage.clientHeight;
    const videoW = this.video.videoWidth || 640;
    const videoH = this.video.videoHeight || 480;

    const stageAspect = stageW / stageH;
    const videoAspect = videoW / videoH;

    let drawW;
    let drawH;
    let offsetX;
    let offsetY;

    if (videoAspect > stageAspect) {
      drawH = stageH;
      drawW = drawH * videoAspect;
      offsetX = (stageW - drawW) * 0.5;
      offsetY = 0;
    } else {
      drawW = stageW;
      drawH = drawW / videoAspect;
      offsetX = 0;
      offsetY = (stageH - drawH) * 0.5;
    }

    return {
      x: (1 - lm.x) * drawW + offsetX,
      y: lm.y * drawH + offsetY
    };
  }

  projectWorldToScreen(worldPos) {
    if (!this.camera) return null;

    const stageW = this.stage.clientWidth;
    const stageH = this.stage.clientHeight;

    const projected = this._tmpScreenVec.copy(worldPos).project(this.camera);

    return {
      x: ((projected.x + 1) * 0.5) * stageW,
      y: ((-projected.y + 1) * 0.5) * stageH
    };
  }

  getModelAnchorWorldPosition(earKey) {
    const node = this.earNodes[earKey];
    if (!node || !node.trackingAnchor) return null;

    node.trackingAnchor.updateWorldMatrix(true, true);
    return node.trackingAnchor.getWorldPosition(new THREE.Vector3());
  }

  updateReprojectionCorrection(earKey) {
    const node = this.earNodes[earKey];
    if (!node || !node.hearingAidRoot || !this.faceAnchor || !this.camera) return;

    const target2D = this.getLandmarkScreenPosition(this.getEarAnchorIndex(earKey));
    const currentWorld = this.getModelAnchorWorldPosition(earKey);

    if (!target2D || !currentWorld) return;

    const projected2D = this.projectWorldToScreen(currentWorld);
    if (!projected2D) return;

    const errorPxX = target2D.x - projected2D.x;
    const errorPxY = target2D.y - projected2D.y;

    const desiredLocalX = THREE.MathUtils.clamp(
      errorPxX * this.REPROJECTION_STRENGTH_X,
      -this.REPROJECTION_MAX_LOCAL_X,
      this.REPROJECTION_MAX_LOCAL_X
    );

    const desiredLocalY = THREE.MathUtils.clamp(
      -errorPxY * this.REPROJECTION_STRENGTH_Y,
      -this.REPROJECTION_MAX_LOCAL_Y,
      this.REPROJECTION_MAX_LOCAL_Y
    );

    node.targetReprojectionOffset.set(desiredLocalX, desiredLocalY, 0);

    if (!node.hearingAidRoot.visible) {
      node.smoothedReprojectionOffset.copy(node.targetReprojectionOffset);
    } else if (this.smoothingEnabled) {
      node.smoothedReprojectionOffset.lerp(
        node.targetReprojectionOffset,
        this.REPROJECTION_SMOOTH
      );
    } else {
      node.smoothedReprojectionOffset.copy(node.targetReprojectionOffset);
    }
  }

  updateFaceAnchorFromMatrix(result, dt) {
    const matrixes = result?.facialTransformationMatrixes;
    const raw = matrixes?.[0]?.data;

    if (!raw || raw.length !== 16 || !this.faceAnchor) {
      if (this.faceAnchor) this.faceAnchor.visible = false;
      return false;
    }

    this.faceMatrix.fromArray(raw);
    this.faceMatrix.premultiply(this.fixMirrorMatrix);

    this.faceMatrix.decompose(
      this.targetFacePosition,
      this.targetFaceQuaternion,
      this.targetFaceScale
    );

    if (!this.faceAnchor.visible) {
      this.smoothedFacePosition.copy(this.targetFacePosition);
      this.smoothedFaceQuaternion.copy(this.targetFaceQuaternion);
      this.smoothedFaceScale.copy(this.targetFaceScale);
    } else if (this.smoothingEnabled) {
      const posAlpha = this.getFollowAlpha(this.FACE_POS_FOLLOW_SPEED, dt);
      const rotAlpha = this.getFollowAlpha(this.FACE_ROT_FOLLOW_SPEED, dt);
      const scaleAlpha = this.getFollowAlpha(this.FACE_SCALE_FOLLOW_SPEED, dt);

      this.smoothedFacePosition.lerp(this.targetFacePosition, posAlpha);
      this.smoothedFaceQuaternion.slerp(this.targetFaceQuaternion, rotAlpha);
      this.smoothedFaceScale.lerp(this.targetFaceScale, scaleAlpha);
    } else {
      this.smoothedFacePosition.copy(this.targetFacePosition);
      this.smoothedFaceQuaternion.copy(this.targetFaceQuaternion);
      this.smoothedFaceScale.copy(this.targetFaceScale);
    }

    this.faceAnchor.position.copy(this.smoothedFacePosition);
    this.faceAnchor.quaternion.copy(this.smoothedFaceQuaternion);
    this.faceAnchor.scale.copy(this.smoothedFaceScale);
    this.faceAnchor.visible = true;

    return true;
  }

  applyEarOccluderForKey(earKey) {
    const node =
      this.earNodes[earKey];

    if (
      !node ||
      !node.earOcc ||
      !node.earOccTop ||
      !node.earOccCanal
    ) {
      return;
    }

    node.earOcc.visible =
      !!this.occlusion.enabled;

    if (!this.occlusion.enabled) {
      return;
    }

    const material =
      this.occlusion.devMode
        ? this.occDebugMat
        : this.occDepthMat;

    node.earOccTop.material =
      material;

    node.earOccCanal.material =
      material;

    const isZeal =
      this.isCurrentProductZeal();


    /*
    * ============================
    * OTICON ZEAL
    * ============================
    *
    * Não utiliza o plano Topo.
    * Utiliza apenas Canal com
    * calibração própria.
    */
    if (isZeal) {
      node.earOccTop.visible = false;
      node.earOccCanal.visible = true;

      const state =
        this.zealOcclusionState[earKey].canal;

      node.earOccCanal.position.set(
        state.position.x,
        state.position.y,
        state.position.z
      );

      node.earOccCanal.rotation.set(
        state.rotation.x,
        state.rotation.y,
        state.rotation.z
      );

      node.earOccCanal.scale.set(
        state.scale.x,
        state.scale.y,
        1
      );

      return;
    }


    /*
    * ============================
    * RESTANTES APARELHOS
    * ============================
    */

    node.earOccTop.visible = true;
    node.earOccCanal.visible = true;

    const state =
      this.occlusionState[earKey];


    /*
    * PLANO TOPO
    */
    node.earOccTop.position.set(
      state.top.position.x,
      state.top.position.y,
      state.top.position.z
    );

    node.earOccTop.rotation.set(
      state.top.rotation.x,
      state.top.rotation.y,
      state.top.rotation.z
    );

    node.earOccTop.scale.set(
      state.top.scale.x,
      state.top.scale.y,
      1
    );


    /*
    * PLANO CANAL
    */
    node.earOccCanal.position.set(
      state.canal.position.x,
      state.canal.position.y,
      state.canal.position.z
    );

    node.earOccCanal.rotation.set(
      state.canal.rotation.x,
      state.canal.rotation.y,
      state.canal.rotation.z
    );

    node.earOccCanal.scale.set(
      state.canal.scale.x,
      state.canal.scale.y,
      1
    );
  }

  getProductPositionForDevice(earKey) {
    const product =
      this.currentProduct;

    const activeDefault =
      this.getEarStateByKey(earKey);

    /*
    * Sem produto:
    * usar simplesmente o default ativo.
    */
    if (!product) {
      return {
        ...activeDefault.positionOffset
      };
    }

    /*
    * Valores guardados nos metafields.
    *
    * Estes valores são considerados
    * calibração DESKTOP.
    */
    const productDesktopPosition =
      earKey === 'left'
        ? {
            x: product.posLeftX,
            y: product.posLeftY,
            z: product.posLeftZ
          }
        : {
            x: product.posRightX,
            y: product.posRightY,
            z: product.posRightZ
          };

    /*
    * Em desktop usamos os metafields
    * exatamente como estão.
    */
    if (!this.isMobile) {
      const desktopDefault =
        this.earStateDesktop[earKey];

      return {
        x:
          productDesktopPosition.x ??
          desktopDefault.positionOffset.x,

        y:
          productDesktopPosition.y ??
          desktopDefault.positionOffset.y,

        z:
          productDesktopPosition.z ??
          desktopDefault.positionOffset.z
      };
    }

    /*
    * MOBILE
    *
    * Calculamos quanto o produto se afastou
    * do default DESKTOP e aplicamos exatamente
    * esse desvio ao default MOBILE.
    */
    const desktopDefault =
      this.earStateDesktop[earKey]
        .positionOffset;

    const mobileDefault =
      this.earStateMobile[earKey]
        .positionOffset;

    const adaptAxis = (
      productValue,
      desktopValue,
      mobileValue
    ) => {
      /*
      * Sem metafield neste eixo:
      * usar diretamente o default mobile.
      */
      if (productValue == null) {
        return mobileValue;
      }

      const desktopDelta =
        productValue - desktopValue;

      return mobileValue + desktopDelta;
    };

    return {
      x: adaptAxis(
        productDesktopPosition.x,
        desktopDefault.x,
        mobileDefault.x
      ),

      y: adaptAxis(
        productDesktopPosition.y,
        desktopDefault.y,
        mobileDefault.y
      ),

      z: adaptAxis(
        productDesktopPosition.z,
        desktopDefault.z,
        mobileDefault.z
      )
    };
  }

  applyEarStateToPivotsForKey(earKey) {
    const node = this.earNodes[earKey];
    if (!node || !node.modelPivot || !node.occlusionPivot) return;

    const st = this.getEarStateByKey(earKey);

    const dynamicScale =
      this.getEarScreenScaleMultiplier(earKey);

    const finalSize =
      st.sizeScale * dynamicScale;

    /*
    * A posição do metafield é a posição REAL/base
    * deste produto, e não um offset adicional.
    *
    * Se o metafield estiver vazio, usamos a posição
    * default do earState.
    */
    const productPosition =
      this.getProductPositionForDevice(
        earKey
      );

    /*
    * Aplicamos a mesma lógica dinâmica que já existia
    * para a posição normal do aparelho.
    */
    const positionState = {
      ...st,
      positionOffset: productPosition
    };

    const scaledPosition =
      this.getScaledPositionOffset(
        positionState,
        dynamicScale
      );

    const px =
      scaledPosition.x +
      node.smoothedReprojectionOffset.x;

    const py =
      scaledPosition.y +
      node.smoothedReprojectionOffset.y;

    const pz =
      scaledPosition.z +
      node.smoothedReprojectionOffset.z;

    node.modelPivot.scale.set(finalSize, finalSize, finalSize);
    node.modelPivot.position.set(px, py, pz);
    node.modelPivot.rotation.set(
      st.rotationOffset.x,
      st.rotationOffset.y,
      st.rotationOffset.z
    );

    node.occlusionPivot.scale.set(finalSize, finalSize, finalSize);
    node.occlusionPivot.position.set(px, py, pz);
    node.occlusionPivot.rotation.set(
      st.rotationOffset.x,
      st.rotationOffset.y,
      st.rotationOffset.z
    );

    if (node.modelHolder) {
      if (earKey === 'right') {
        node.modelHolder.rotation.set(0, 0, 0);
        node.modelHolder.scale.set(-1, 1, 1);
      } else {
        node.modelHolder.rotation.set(0, Math.PI, 0);
        node.modelHolder.scale.set(1, 1, 1);
      }
    }

    this.applyEarOccluderForKey(earKey);
  }

  applyAllEarStates() {
    this.applyEarStateToPivotsForKey('right');
    this.applyEarStateToPivotsForKey('left');

    this.applyFaceOccluder();
  }

  async loadGLBOnce() {
    const loader = new GLTFLoader();

    const gltf = await new Promise((resolve, reject) => {
      loader.load(
        this.GLB_FILE,
        resolve,
        (p) => {
          if (p.total) this.updateStatus(`📦 GLB: ${Math.round((p.loaded / p.total) * 100)}%`);
        },
        reject
      );
    });

    const prepareModel = (model) => {
      const meshesBox = new THREE.Box3();
      const tmpBox = new THREE.Box3();
      let hasMesh = false;

      model.updateWorldMatrix(true, true);
      model.traverse((o) => {
        if (o.isMesh) {
          hasMesh = true;
          tmpBox.setFromObject(o);
          meshesBox.union(tmpBox);
        }
      });

      const box = hasMesh ? meshesBox : new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      model.position.sub(center);

      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const baseScale = this.TARGET_MODEL_SIZE_M / maxDim;
      model.scale.setScalar(baseScale);

      if (this.currentProduct) {
        model.rotation.x +=
          THREE.MathUtils.degToRad(
            this.currentProduct.rotationX || 0
          );

        model.rotation.y +=
          THREE.MathUtils.degToRad(
            this.currentProduct.rotationY || 0
          );

        model.rotation.z +=
          THREE.MathUtils.degToRad(
            this.currentProduct.rotationZ || 0
          );

        if (this.currentProduct.scale > 0) {
          model.scale.multiplyScalar(
            this.currentProduct.scale
          );
        }

        /*
        * OTICON ZEAL
        *
        * Espelhar o próprio GLB para inverter
        * o lado para onde sai o fio.
        */
        if (this.isCurrentProductZeal()) {
          model.scale.z *= -1;
        }
      }

      model.traverse((o) => {
        if (o.isMesh && o.material) {
          o.renderOrder = 10;
          o.frustumCulled = false;
        }
      });

      return model;
    };

    ['right', 'left'].forEach((earKey) => {
      const node = this.earNodes[earKey];
      node.modelHolder.clear();

      const modelClone = prepareModel(gltf.scene.clone(true));
      node.modelHolder.add(modelClone);
      this.rebuildTrackingAnchorForKey(earKey);
    });

    this.applyAllEarStates();
  }

  updateTrackingFromResult(result, dt) {
    const faceOk = this.updateFaceAnchorFromMatrix(result, dt);
    if (!faceOk || !this.faceAnchor) {
      Object.values(this.earNodes).forEach((node) => {
        if (node.hearingAidRoot) node.hearingAidRoot.visible = false;
      });
      return;
    }

    ['right', 'left'].forEach((earKey) => {
      const node = this.earNodes[earKey];
      const earLocalPos = this.getEarLocalPosition(earKey);

      if (!earLocalPos || !node.hearingAidRoot) {
        if (node.hearingAidRoot) node.hearingAidRoot.visible = false;
        return;
      }

      if (!node.hearingAidRoot.visible) {
        node.smoothedEarLocalPosition.copy(earLocalPos);
        node.smoothedReprojectionOffset.set(0, 0, 0);
        node.targetReprojectionOffset.set(0, 0, 0);
      } else if (this.smoothingEnabled) {
        const earAlpha = this.getFollowAlpha(this.EAR_POS_FOLLOW_SPEED, dt);
        node.smoothedEarLocalPosition.lerp(earLocalPos, earAlpha);
      } else {
        node.smoothedEarLocalPosition.copy(earLocalPos);
      }

      node.hearingAidRoot.position.copy(node.smoothedEarLocalPosition);
      node.hearingAidRoot.quaternion.identity();
      node.hearingAidRoot.visible = true;

      const earVisible = this.isEarLikelyVisible(earKey);
      node.modelPivot.visible = earVisible;
      node.occlusionPivot.visible = earVisible && this.occlusion.enabled;

      this.applyEarStateToPivotsForKey(earKey);
      this.updateReprojectionCorrection(earKey);
      this.applyEarStateToPivotsForKey(earKey);
    });

    this.applyFaceOccluder();

    if (!this.occlusionCalibrationActive) {
      this.updateCalibrationStatus('✅ AR ativo!');
    }
  }

  updateDebugDot() {
    if (!this.debugEnabled || !this.debugDot || !this.latestLandmarks?.length || !this.video) {
      if (this.debugDot) this.debugDot.style.display = 'none';
      return;
    }

    const p = this.getLandmarkScreenPosition(this.debugLandmarkIndex);
    if (!p) {
      this.debugDot.style.display = 'none';
      return;
    }

    this.debugDot.style.display = 'block';
    this.debugDot.style.left = `${p.x}px`;
    this.debugDot.style.top = `${p.y}px`;
  }

  toggleDebug(button) {
    this.debugEnabled =
      !this.debugEnabled;

    if (button) {
      button.textContent =
        `🔴 Debug point: ${
          this.debugEnabled
            ? 'ON'
            : 'OFF'
        }`;
    }

    this.debugLandmarkIndex =
      this.getCurrentEarAnchorIndex();

    this.updateDebugDot();

    this.updateCalibrationStatus(
      '🔴 Debug'
    );
  }

  toggleOcclusionDevMode(button) {
    this.occlusion.devMode =
      !this.occlusion.devMode;

    if (button) {
      button.textContent =
        `🟥 Occlusion DEV: ${
          this.occlusion.devMode
            ? 'ON'
            : 'OFF'
        }`;
    }

    this.applyEarOccluderForKey('right');
    this.applyEarOccluderForKey('left');

    /*
    * Atualizar também o Face Occluder
    */
    this.applyFaceOccluder();

    this.updateOcclusionCalibrationStatus(
      '🟥 Occlusion DEV'
    );
  }

  adjustOcclusionPosition(direction) {
    const state =
      this.getCurrentOcclusionState();

    switch (direction) {
      case 'up':
        state.position.y += this.OCC_POS_STEP;
        break;

      case 'down':
        state.position.y -= this.OCC_POS_STEP;
        break;

      case 'forward':
        state.position.z += this.OCC_POS_STEP;
        break;

      case 'back':
        state.position.z -= this.OCC_POS_STEP;
        break;

      case 'left':
        state.position.x -= this.OCC_POS_STEP;
        break;

      case 'right':
        state.position.x += this.OCC_POS_STEP;
        break;
    }

    this.applyEarOccluderForKey(
      this.getCurrentEarKey()
    );

    this.updateOcclusionCalibrationStatus(
      '📍 Occlusion posição'
    );
  }

  adjustOcclusionScale(axis, direction) {
    const state =
      this.getCurrentOcclusionState();

    const multiplier =
      direction === 'plus'
        ? 1 + this.OCC_SCALE_STEP
        : 1 - this.OCC_SCALE_STEP;

    state.scale[axis] *= multiplier;

    state.scale[axis] = THREE.MathUtils.clamp(
      state.scale[axis],
      0.001,
      1
    );

    this.applyEarOccluderForKey(
      this.getCurrentEarKey()
    );

    this.updateOcclusionCalibrationStatus(
      '📐 Occlusion escala'
    );
  }

  adjustOcclusionRotation(axis, direction) {
    const state =
      this.getCurrentOcclusionState();

    const step =
      THREE.MathUtils.degToRad(
        this.OCC_ROT_STEP_DEG
      ) *
      (
        direction === 'plus'
          ? 1
          : -1
      );

    state.rotation[axis] += step;

    this.applyEarOccluderForKey(
      this.getCurrentEarKey()
    );

    this.updateOcclusionCalibrationStatus(
      '🧭 Occlusion rotação'
    );
  }

  updateOcclusionCalibrationStatus(
    prefix = '🕳️ Occlusion'
  ) {
    this.occlusionCalibrationActive = true;

    const earKey =
      this.getCurrentEarKey();

    const planeKey =
      this.occlusion.selectedPlane;

    const state =
      this.getCurrentOcclusionState();

    const rx = Math.round(
      THREE.MathUtils.radToDeg(
        state.rotation.x
      )
    );

    const ry = Math.round(
      THREE.MathUtils.radToDeg(
        state.rotation.y
      )
    );

    const rz = Math.round(
      THREE.MathUtils.radToDeg(
        state.rotation.z
      )
    );

    this.updateStatus(
      `${prefix}\n` +
      `ear=${earKey}\n` +
      `plane=${
        this.isCurrentProductZeal()
          ? 'canal'
          : planeKey
      }\n` +
      `config=${
        this.isCurrentProductZeal()
          ? 'ZEAL'
          : 'DEFAULT'
      }\n` +
      `produto=${this.currentProduct?.title || 'manual'}\n` +
            `pos x=${state.position.x.toFixed(3)} ` +
      `y=${state.position.y.toFixed(3)} ` +
      `z=${state.position.z.toFixed(3)}\n` +
      `scale x=${state.scale.x.toFixed(3)} ` +
      `y=${state.scale.y.toFixed(3)}\n` +
      `rot x=${rx}° y=${ry}° z=${rz}°\n` +
      `dev=${this.occlusion.devMode ? 'ON' : 'OFF'}`
    );
  }

  async copyOcclusionCalibrationValues(button) {
    const earKey =
      this.getCurrentEarKey();

    const planeKey =
      this.occlusion.selectedPlane;

    const state =
      this.getCurrentOcclusionState();

    const rx =
      THREE.MathUtils.radToDeg(
        state.rotation.x
      );

    const ry =
      THREE.MathUtils.radToDeg(
        state.rotation.y
      );

    const rz =
      THREE.MathUtils.radToDeg(
        state.rotation.z
      );

    const format = (value) =>
      Number(value).toFixed(3);

    const formatDeg = (value) =>
      Number(value).toFixed(1).replace(/\.0$/, '');

    const configName =
      this.isCurrentProductZeal()
        ? 'zealOcclusionState'
        : 'occlusionState';

    const actualPlaneKey =
      this.isCurrentProductZeal()
        ? 'canal'
        : planeKey;

    const text =
    `// ${configName}.${earKey}.${actualPlaneKey}
    ${earKey}: {
      ${actualPlaneKey}: {
        position: {
          x: ${format(state.position.x)},
          y: ${format(state.position.y)},
          z: ${format(state.position.z)}
        },
        scale: {
          x: ${format(state.scale.x)},
          y: ${format(state.scale.y)},
          z: 1
        },
        rotation: {
          x: THREE.MathUtils.degToRad(${formatDeg(rx)}),
          y: THREE.MathUtils.degToRad(${formatDeg(ry)}),
          z: THREE.MathUtils.degToRad(${formatDeg(rz)})
        }
      }
    }`;

    try {
      await navigator.clipboard.writeText(text);

      if (button) {
        const oldText = button.textContent;

        button.textContent = '✅ Copiado';

        setTimeout(() => {
          button.textContent = oldText;
        }, 1500);
      }

      this.updateOcclusionCalibrationStatus(
        '📋 Valores copiados'
      );

      console.log(
        `Occlusion calibration ${earKey}.${planeKey}:\n${text}`
      );
    } catch (error) {
      console.error(
        'Erro ao copiar calibração:',
        error
      );

      this.updateStatus(
        '❌ Não foi possível copiar os valores.'
      );
    }
  }

  stepDebugLandmark(step) {
    const max = this.latestLandmarks?.length ? this.latestLandmarks.length - 1 : 477;
    this.debugLandmarkIndex += step;
    if (this.debugLandmarkIndex < 0) this.debugLandmarkIndex = max;
    if (this.debugLandmarkIndex > max) this.debugLandmarkIndex = 0;
    this.updateDebugDot();
    this.updateStatus(`🔴 Debug landmark=${this.debugLandmarkIndex}`);
  }

  toggleSmoothing(button) {
    this.smoothingEnabled = !this.smoothingEnabled;
    if (button) button.textContent = `🧈 Smoothing: ${this.smoothingEnabled ? 'ON' : 'OFF'}`;
    this.updateCalibrationStatus('🧈 Smoothing');
  }

  isCurrentProductZeal() {
    const title =
      this.normalizeFilterValue(
        this.currentProduct?.title
      );

    return title.includes('oticon zeal');
  }

  getCurrentOcclusionState() {
    const earKey =
      this.getCurrentEarKey();

    /*
    * ZEAL
    *
    * Só existe o plano Canal e utilizamos
    * a calibração específica do Zeal.
    */
    if (this.isCurrentProductZeal()) {
      return this.zealOcclusionState[earKey].canal;
    }

    /*
    * RESTANTES APARELHOS
    */
    const planeKey =
      this.occlusion.selectedPlane;

    return this.occlusionState[earKey][planeKey];
  }

  switchOcclusionPlane(button) {
    /*
    * O Zeal não tem plano Topo.
    */
    if (this.isCurrentProductZeal()) {
      this.occlusion.selectedPlane =
        'canal';

      if (button) {
        button.textContent =
          '🟥 Plano: Canal';
      }

      this.updateOcclusionCalibrationStatus(
        '🔄 Zeal utiliza apenas Canal'
      );

      return;
    }

    /*
    * Restantes aparelhos:
    * alternar Topo / Canal.
    */
    this.occlusion.selectedPlane =
      this.occlusion.selectedPlane === 'top'
        ? 'canal'
        : 'top';

    if (button) {
      button.textContent =
        this.occlusion.selectedPlane === 'top'
          ? '🟥 Plano: Topo'
          : '🟥 Plano: Canal';
    }

    this.updateOcclusionCalibrationStatus(
      '🔄 Plano selecionado'
    );
  }

  toggleOcclusion(button) {
    this.occlusion.enabled =
      !this.occlusion.enabled;

    if (button) {
      button.textContent =
        `🕳️ Occlusion: ${
          this.occlusion.enabled
            ? 'ON'
            : 'OFF'
        }`;
    }

    /*
    * Planos agregados aos aparelhos
    */
    this.applyEarOccluderForKey('right');
    this.applyEarOccluderForKey('left');

    /*
    * Occluder global da cara
    */
    this.applyFaceOccluder();

    this.updateOcclusionCalibrationStatus(
      '🕳️ Occlusion'
    );
  }

  adjustSize(direction) {
    this.occlusionCalibrationActive = false;
    const st = this.getCurrentState();

    st.sizeScale *= direction === 'bigger' ? 1.1 : 0.9;
    st.sizeScale = Math.max(0.05, Math.min(80.0, st.sizeScale));

    this.applyAllEarStates();
    this.updateCalibrationStatus('📏 Tamanho');
  }

  convertDevicePositionToDesktop(
    earKey,
    position
  ) {
    /*
    * Em desktop já estamos no sistema
    * de coordenadas dos metafields.
    */
    if (!this.isMobile) {
      return { ...position };
    }

    const desktopDefault =
      this.earStateDesktop[earKey]
        .positionOffset;

    const mobileDefault =
      this.earStateMobile[earKey]
        .positionOffset;

    /*
    * Inverso de:
    *
    * mobile =
    * mobileDefault +
    * (productDesktop - desktopDefault)
    */
    return {
      x:
        desktopDefault.x +
        (position.x - mobileDefault.x),

      y:
        desktopDefault.y +
        (position.y - mobileDefault.y),

      z:
        desktopDefault.z +
        (position.z - mobileDefault.z)
    };
  }

  adjustPosition(direction) {
    this.occlusionCalibrationActive = false;
    const earKey = this.getCurrentEarKey();
    const st = this.getCurrentState();

    let position =
      this.getProductPositionForDevice(
        earKey
      );

    switch (direction) {
      case 'up':
        position.y += this.POS_STEP;
        break;

      case 'down':
        position.y -= this.POS_STEP;
        break;

      case 'forward':
        position.z += this.POS_STEP;
        break;

      case 'back':
        position.z -= this.POS_STEP;
        break;

      case 'left':
        position.x -= this.POS_STEP;
        break;

      case 'right':
        position.x += this.POS_STEP;
        break;
    }

    if (this.currentProduct) {
      const desktopPosition =
        this.convertDevicePositionToDesktop(
          earKey,
          position
        );

      if (earKey === 'left') {
        this.currentProduct.posLeftX =
          desktopPosition.x;

        this.currentProduct.posLeftY =
          desktopPosition.y;

        this.currentProduct.posLeftZ =
          desktopPosition.z;
      } else {
        this.currentProduct.posRightX =
          desktopPosition.x;

        this.currentProduct.posRightY =
          desktopPosition.y;

        this.currentProduct.posRightZ =
          desktopPosition.z;
      }
    } else {
      st.positionOffset = position;
    }

    this.applyAllEarStates();
    this.updateCalibrationStatus('📍 Posição');
  }

  adjustRotation(axis, dir) {
    this.occlusionCalibrationActive = false;
    const st = this.getCurrentState();
    const step = THREE.MathUtils.degToRad(this.ROT_STEP_DEG) * (dir === 'plus' ? 1 : -1);

    st.rotationOffset[axis] += step;

    this.applyAllEarStates();
    this.updateCalibrationStatus('🧭 Rotação');
  }

  resetAll() {
    const defaults = this.isMobile ? this.earStateMobile : this.earStateDesktop;
    this.earState = this.cloneEarState(defaults);

    this.earNodes.right.smoothedReprojectionOffset.set(0, 0, 0);
    this.earNodes.right.targetReprojectionOffset.set(0, 0, 0);

    this.earNodes.left.smoothedReprojectionOffset.set(0, 0, 0);
    this.earNodes.left.targetReprojectionOffset.set(0, 0, 0);

    this.applyAllEarStates();
    this.updateCalibrationStatus('🎯 Reset');
  }

  switchEar() {
    this.occlusionCalibrationActive = false;
    this.isRightEar = !this.isRightEar;
    this.debugLandmarkIndex = this.getCurrentEarAnchorIndex();
    this.updateDebugDot();
    this.updateCalibrationStatus('👂 Orelha de edição');
  }

  resize() {
    if (!this.renderer || !this.camera) return;
    const w = this.stage.clientWidth;
    const h = this.stage.clientHeight;

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.updateDebugDot();
  }

  async processFrame() {
    if (!this.running || !this.faceLandmarker || !this.video) return;

    try {
      if (this.video.readyState >= 2) {
        const nowMs = performance.now();
        const dt = this.getDtSeconds(nowMs);

        if (this.video.currentTime !== this.lastVideoTime) {
          const result = this.faceLandmarker.detectForVideo(this.video, nowMs);
          this.lastVideoTime = this.video.currentTime;

          if (result?.faceLandmarks?.length) {
            this.latestLandmarks = result.faceLandmarks[0];
            this.updateTrackingFromResult(result, dt);
          } else {
            this.latestLandmarks = null;
            if (this.faceAnchor) this.faceAnchor.visible = false;

            Object.values(this.earNodes).forEach((node) => {
              if (node.hearingAidRoot) node.hearingAidRoot.visible = false;
            });
          }

          this.updateDebugDot();
        }
      }
    } catch (err) {
      console.warn('Erro MediaPipe frame:', err);
    }

    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }

    requestAnimationFrame(() => this.processFrame());
  }

  async init() {
    if (this.STOREFRONT_TOKEN) {
      try {
        console.log('AR token:', this.STOREFRONT_TOKEN);
        console.log('AR shop domain:', this.SHOP_DOMAIN);
        console.log('Antes de fetchARProducts');

        this.allARProducts = await this.fetchARProducts();

        console.log('Produtos recebidos:', this.allARProducts);

        this.arProducts =
          this.filterProductsByHearingLevel(
            this.allARProducts
          );

        console.log(
          'Produtos filtrados:',
          this.arProducts
        );

        console.log(
          'Filtros:',
          this.productFilters
        );

        console.log(
          'Níveis auditivos:',
          {
            left: this.leftHearingLevel,
            right: this.rightHearingLevel,
            requires85: this.requires85,
            requires100: this.requires100
          }
        );

        if (this.arProducts.length) {
          this.currentProduct = this.arProducts[0];
          this.selectedProductIndex = 0;
          this.GLB_FILE = this.currentProduct.glbUrl || this.DEFAULT_GLB_FILE;
          this.renderProductSelector();
        } else {
          this.GLB_FILE = '';
          this.updateStatus(
            '❌ Não foram encontrados aparelhos compatíveis com este perfil auditivo.'
          );
        }
      } catch (error) {
        console.error('Erro ao carregar produtos AR:', error);
        this.updateStatus(`❌ Erro ao carregar produtos:\n${error?.message || error}`);
      }
    }

    if (!this.GLB_FILE) {
      this.updateStatus('❌ Falta configurar a URL do GLB na section');
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      this.updateStatus('❌ Câmara não suportada neste browser');
      return;
    }

    try {
      this.updateStatus('🚀 A iniciar câmara...');
      await this.setupCamera();

      this.updateStatus('🧠 A carregar MediaPipe...');
      await this.setupMediaPipe();

      this.updateStatus('🎬 A preparar Three.js...');
      this.setupThree();

      this.updateStatus('📦 A carregar modelo 3D...');
      await this.loadGLBOnce();
      this.applyAllEarStates();

      this.resize();
      window.addEventListener('resize', () => this.resize());

      this.running = true;
      this.updateCalibrationStatus('✅ AR ativo!');
      this.processFrame();
    } catch (err) {
      console.error(err);
      this.updateStatus(`❌ Erro a iniciar AR:\n${err?.message || err}`);
    }
  }
}

function initARSystem(root, shouldScroll = true, options = {}) {
  if (!root || root.dataset.arReady === 'true') return;

  root.classList.remove('is-hidden');
  root.dataset.arReady = 'true';

  if (options.hearingLevel) {
    root.dataset.hearingLevel = options.hearingLevel;
  }

  const instance = new ShopifyARSystem(root, options);
  instance.init();

  if (shouldScroll) {
    setTimeout(() => {
      root.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 150);
  }
}

function initAllARSystems() {
  document.querySelectorAll('.ar-system[data-section-id]').forEach((root) => {
    const isHidden = root.classList.contains('is-hidden');
    const autostart = root.dataset.autostart === 'true';

    if (isHidden) return;

    if (autostart) {
      initARSystem(root, false);
    }
  });
}

document.addEventListener(
  'customer-exam:start-ar',
  (event) => {
    const roots =
      document.querySelectorAll(
        '.ar-system[data-section-id]'
      );

    let target = null;

    roots.forEach((root) => {
      if (
        !target &&
        root.classList.contains('is-hidden')
      ) {
        target = root;
      }
    });

    if (!target) {
      target =
        document.querySelector(
          '.ar-system[data-section-id]'
        );
    }

    initARSystem(target, true, {
      leftHearingLevel:
        event?.detail?.leftHearingLevel ||
        event?.detail?.analysis?.left_hearing_level ||
        '',

      rightHearingLevel:
        event?.detail?.rightHearingLevel ||
        event?.detail?.analysis?.right_hearing_level ||
        '',

      selectedEar:
        event?.detail?.selectedEar ||
        '',

      requires85:
        event?.detail?.requires85 === true,

      requires100:
        event?.detail?.requires100 === true,

      answers:
        event?.detail?.answers || {},

      productFilters:
        event?.detail?.productFilters || {}
    });
  }
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllARSystems);
} else {
  initAllARSystems();
}

document.addEventListener('shopify:section:load', initAllARSystems);